import { http } from "@/lib/request";
import { auth } from "@/lib/auth";
import { buildApiUrl } from "@/config/env";
import { RequestError } from "@/lib/request";

export interface AssistantConversation {
  conversation_id: number;
  conversation_no?: string;
  scene?: string;
  title?: string;
  status?: number;
  latest_intent?: string;
  message_count?: number;
  last_message_time?: string;
}

export interface AssistantConversationListResult {
  lists?: AssistantConversation[];
  count?: number;
}

export interface AssistantMessage {
  message_id: number;
  conversation_id?: number;
  scene?: string;
  role?: string;
  content?: string;
  content_digest?: string;
  intent_code?: string;
  risk_level?: string;
  model_name?: string;
  create_time?: string;
}

export interface AssistantServiceRecommendation {
  goods_id?: number;
  name?: string;
  category_id?: number;
  category_name?: string;
  price?: number;
  duration?: number;
  is_append_goods?: number;
  tags?: string;
  match_reason?: string;
}

export interface AssistantCoachRecommendation {
  coach_id?: number;
  name?: string;
  work_photo?: string;
  order_num?: number;
  rating?: number;
  city_id?: number;
  city_name?: string;
  recommend_reason?: string;
}

export interface AssistantChatResult {
  conversation_id?: number;
  conversation_no?: string;
  scene?: string;
  user_message_id?: number;
  assistant_message_id?: number;
  reply?: string;
  intent_code?: string;
  risk_level?: string;
  summary_task_created?: boolean;
  summary_task_id?: number;
  recommended_goods_ids?: number[];
  recommended_services?: AssistantServiceRecommendation[];
  recommended_coaches?: AssistantCoachRecommendation[];
  rate_limited?: boolean;
  daily_limit?: number;
  daily_used?: number;
  rag_degraded?: boolean;
  model_degraded?: boolean;
  degrade_reason?: string;
}

export interface AssistantChatStreamHandlers {
  onStart?: (result: AssistantChatResult) => void;
  onDelta?: (content: string) => void;
  onDone?: (result: AssistantChatResult) => void;
  onError?: (message: string) => void;
}

export interface AssistantChatPayload {
  conversation_id?: number;
  conversation_no?: string;
  scene?: string;
  city_name?: string;
  longitude?: number;
  latitude?: number;
  content: string;
}

const parseAssistantStreamPayload = (value: string) => {
  if (!value.trim()) {
    return {};
  }
  return JSON.parse(value);
};

const handleAssistantStreamEvent = (
  rawEvent: string,
  handlers: AssistantChatStreamHandlers,
) => {
  const lines = rawEvent.split(/\r?\n/);
  let eventName = "message";
  const dataLines: string[] = [];
  lines.forEach((line) => {
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  });
  const payload = parseAssistantStreamPayload(dataLines.join("\n"));
  if (eventName === "start") {
    handlers.onStart?.(payload as AssistantChatResult);
    return;
  }
  if (eventName === "delta") {
    handlers.onDelta?.(String((payload as { content?: string }).content || ""));
    return;
  }
  if (eventName === "done") {
    handlers.onDone?.(payload as AssistantChatResult);
    return;
  }
  if (eventName === "error") {
    handlers.onError?.(String((payload as { message?: string }).message || "服务咨询暂不可用，请稍后再试。"));
  }
};

export const assistantApi = {
  getConversations(params?: { page_no?: number; page_size?: number; scene?: string }) {
    return http.get<AssistantConversationListResult>("/assistant/conversation/lists", { params });
  },

  getMessages(params: { conversation_id?: number; conversation_no?: string; limit?: number; scene?: string }) {
    return http.get<AssistantMessage[]>("/assistant/message/lists", { params });
  },

  chat(payload: AssistantChatPayload) {
    return http.post<AssistantChatResult>("/assistant/chat", payload);
  },

  async chatStream(payload: AssistantChatPayload, handlers: AssistantChatStreamHandlers) {
    const token = auth.getToken();
    const response = await fetch(buildApiUrl("/assistant/chat/stream"), {
      method: "POST",
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
        ...(token ? { Authorization: token, token } : {}),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new RequestError(`HTTP ${response.status}`, response.status);
    }
    const contentType = response.headers.get("Content-Type") || "";
    if (!contentType.includes("text/event-stream")) {
      const result = await response.json();
      throw new RequestError(result?.msg || "Request failed", result?.code, result);
    }
    if (!response.body) {
      throw new RequestError("浏览器不支持流式响应");
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || "";
      events.forEach((event) => handleAssistantStreamEvent(event, handlers));
    }
    if (buffer.trim()) {
      handleAssistantStreamEvent(buffer, handlers);
    }
  },
};
